import { createStore, type StoreApi } from 'zustand/vanilla';

import { type Bet, type BetslipState, type BookieOdds } from '../context/types';

/**
 * Framework-agnostic betslip state core.
 *
 * The non-OC betslip state used to live in seven `useState` hooks inside `BetslipProvider`, which
 * chained it to the React tree. It now lives in a Zustand vanilla store so the state can be read and
 * written without React — the prerequisite for Astro islands driving the betslip post-migration
 * (NGF-6116). `BetslipProvider` is a thin adapter over this core; the OC repub already proves the
 * target shape via `window.ocBetslip`.
 *
 * One store is created per provider (see `betslip-context.tsx`) rather than as a module singleton, so
 * server-rendered, per-request betslip state stays isolated exactly as it was with `useState`. On the
 * client the single provider publishes its store via `setActiveBetslipStore`, giving non-React
 * consumers a stable handle.
 *
 * The store exposes Zustand's standard `StoreApi` directly — read with `.getState()`, write with
 * `.setState({ field })`, subscribe with `.subscribe(listener)`. No bespoke per-field facade.
 */

export type BetslipStoreState = {
  added: boolean;
  betslip: Bet[];
  bookieOdds: BookieOdds[];
  loading: boolean;
  open: boolean;
  selectedBookie: BookieOdds | null;
  stake: number;
};

export type BetslipStore = StoreApi<BetslipStoreState>;

export type BetslipStoreInit = {
  betslip?: Bet[];
  bookieOdds?: BookieOdds[];
  selectedBookie?: BookieOdds | null;
  stake?: number;
};

export const createBetslipStore = (init: BetslipStoreInit = {}): BetslipStore =>
  createStore<BetslipStoreState>(() => ({
    added: false,
    betslip: init.betslip ?? [],
    bookieOdds: init.bookieOdds ?? [],
    loading: false,
    open: false,
    selectedBookie: init.selectedBookie ?? null,
    stake: init.stake ?? 10,
  }));

/**
 * Derive the public betslip snapshot (with computed `betIds` / `count`) from a raw store state. Takes
 * the state rather than the store so React consumers can feed it the `useSyncExternalStore` value
 * directly; non-React callers pass `store.getState()`.
 */
export const getBetslipSnapshot = ({
  added,
  betslip,
  bookieOdds,
  loading,
  open,
  selectedBookie,
  stake,
}: BetslipStoreState): BetslipState => {
  return {
    added,
    betslip,
    betIds: betslip.map(b => b.betId),
    bookieOdds,
    count: betslip.length,
    loading,
    open,
    selectedBookie,
    stake,
  };
};

// Pointer to the live store on the client, where exactly one BetslipProvider is mounted. Lets non-React
// consumers (Astro islands, a future window surface, diagnostics) subscribe to the core directly.
let activeBetslipStore: BetslipStore | null = null;

export const setActiveBetslipStore = (store: BetslipStore | null): void => {
  // Client-only by contract: the pointer exists for browser consumers, and guarding here keeps any
  // future server-side caller (Astro island, test) from leaking one request's store into another.
  if (typeof window === 'undefined') return;
  activeBetslipStore = store;
};

export const getActiveBetslipStore = (): BetslipStore | null => activeBetslipStore;
